import React, {
  useState,
  useContext,
  createContext,
  useEffect,
  useRef,
  memo,
  useCallback,
} from "react";

const GroupContext = createContext();

export const SelectionGroup = memo(({
  SelectedComponent,
  UnselectedComponent,
  defaultItem,
  children,
  className,
  id,
  SelectedItemMask,
}) => {
  // Initialize with defaultItem immediately to prevent flash
  const [selectedItem, setSelectedItem] = useState(defaultItem ?? 0);
  const itemsRef = useRef([]);
  const groupRef = useRef(null);
  const prevDefaultItemRef = useRef(defaultItem);
  const [selectedItemRect, setSelectedItemRect] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  const resizeObserver = useRef();
  
  useEffect(() => {
    if (resizeObserver.current) resizeObserver.current.disconnect();
    resizeObserver.current = new ResizeObserver(() => {
      if (itemsRef.current[selectedItem] && groupRef.current) {
        const itemRect = itemsRef.current[selectedItem].getBoundingClientRect();
        const groupRect = groupRef.current.getBoundingClientRect();
        setSelectedItemRect({
          x: itemRect.x - groupRect.x,
          y: itemRect.y - groupRect.y,
          width: itemRect.width,
          height: itemRect.height,
        });
      }
    });
    if (groupRef.current) {
      resizeObserver.current.observe(groupRef.current);
    }
    return () => {
      if (resizeObserver.current) {
        resizeObserver.current.disconnect();
      }
    };
  }, [selectedItem]);
  
  const handleItemClick = useCallback((index) => {
    setSelectedItem(index);
  }, []);

  // Update selectedItem when defaultItem changes (during navigation)
  // Use ref to detect changes and update synchronously if possible
  useEffect(() => {
    if (prevDefaultItemRef.current !== defaultItem && defaultItem != null) {
      prevDefaultItemRef.current = defaultItem;
      setSelectedItem(defaultItem);
    }
  }, [defaultItem]);

  return (
    <div className={`${className} relative except`} ref={groupRef} id={id}>
      <GroupContext.Provider
        value={{
          SelectedComponent: SelectedComponent,
          UnselectedComponent: UnselectedComponent,
        }}
      >
        <div
    
        ></div>
        {React.Children.map(children, (child, index) => {
          return React.cloneElement(child, {
            selected: index === selectedItem,
            onClick: () => handleItemClick(index),
            setRef: (el) => (itemsRef.current[index] = el),
          });
        })}
      </GroupContext.Provider>
    </div>
  );
});

export const SelectionItem = memo(({
  selected,
  onClick,
  SelectedItem,
  UnselectedItem,
  children,
  setRef,
  disabled,
  onItemClick, // New prop for individual item click handling
}) => {
  const { SelectedComponent, UnselectedComponent } = useContext(GroupContext);
  // Remove delayed state update - make icon changes instant for better UX
  const selectedA = selected;

  const handleClick = useCallback(() => {
    if (!disabled) {
      onClick(); // Call the group's selection handler
      if (onItemClick) {
        onItemClick(); // Call the individual item's click handler
      }
    }
  }, [disabled, onClick, onItemClick]);

  return (
    <div
      onClick={handleClick}
      className="cursor-pointer select-none w-full relative"
      ref={(el) => setRef(el)}
    >
      {selectedA
        ? SelectedItem
          ? SelectedItem
          : SelectedComponent
        : UnselectedItem
        ? UnselectedItem
        : UnselectedComponent}
      {children}
    </div>
  );
});
